import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { authStateManager } from '../lib/authStateManager';
import { useBusiness } from '../contexts/BusinessContext';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { handleSuccessfulLogin } = useBusiness();
  
  // Track previous authentication state to distinguish new logins from session restorations
  const [wasAuthenticated, setWasAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    // Initialize the centralized manager (safe - won't double-initialize)
    authStateManager.initialize();
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { session } = await authStateManager.getSession();
        const hasUser = !!session?.user;
        
        setUser(session?.user ?? null);
        setWasAuthenticated(hasUser); // Track initial auth state
        setLoading(false);
      } catch (error) {
        console.error('Error getting session:', error);
        setUser(null);
        setWasAuthenticated(false);
        setLoading(false);
      }
    };

    getInitialSession();

    // Subscribe to the centralized auth manager (events are pre-filtered)
    const unsubscribe = authStateManager.subscribe((event, session) => {
      // All events that reach here are significant (SIGNED_IN/SIGNED_OUT only)
      if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        setLoading(false);
        
        // ALWAYS trigger business loading when user is signed in
        // The BusinessContext will handle deduplication and prevent unnecessary reloads
        handleSuccessfulLogin();
        
        // Update the authentication tracking state
        setWasAuthenticated(true);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setWasAuthenticated(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []); // Empty dependency - auth listener should only be set up once

  return {
    user,
    loading,
    error: null,
    isAuthenticated: !!user
  };
}